(() => {
  const ROOT = '[data-bridge-gallery]';
  const instances = new WeakMap();

  function build(root) {
    if (instances.has(root)) return;
    if (typeof window.Swiper === 'undefined') return;

    const mainEl = root.querySelector('.bridge-product__main');
    if (!mainEl) return;

    const slideCount = mainEl.querySelectorAll('.swiper-slide').length;
    if (slideCount === 0) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const thumbsEl = root.querySelector('.bridge-product__thumbs');
    let thumbs = null;

    if (thumbsEl) {
      thumbs = new window.Swiper(thumbsEl, {
        slidesPerView: Math.min(3.5, slideCount),
        spaceBetween: 8,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        breakpoints: {
          581: { slidesPerView: Math.min(6, slideCount) },
        },
      });
    }

    const main = new window.Swiper(mainEl, {
      slidesPerView: 1,
      loop: false,
      speed: reduceMotion ? 0 : 400,
      a11y: { enabled: true },
      keyboard: { enabled: true, onlyInViewport: true },
      navigation: {
        prevEl: root.querySelector('.bridge-product__nav-btn--prev'),
        nextEl: root.querySelector('.bridge-product__nav-btn--next'),
      },
      thumbs: thumbs ? { swiper: thumbs } : undefined,
    });

    instances.set(root, { main, thumbs });
  }

  function destroy(root) {
    const entry = instances.get(root);
    if (!entry) return;
    if (entry.main) entry.main.destroy(true, true);
    if (entry.thumbs) entry.thumbs.destroy(true, true);
    instances.delete(root);
  }

  const autoVideos = new WeakSet();

  function setupVideos(scope) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    scope.querySelectorAll('.bridge-product__videos video[autoplay]').forEach((video) => {
      if (autoVideos.has(video)) return;
      autoVideos.add(video);

      video.autoplay = false;
      video.muted = true;
      video.pause();

      if (reduceMotion || typeof window.IntersectionObserver === 'undefined') return;

      const observer = new window.IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            const played = video.play();
            if (played && played.catch) played.catch(() => {});
          } else {
            video.pause();
          }
        },
        { threshold: 0.5 }
      );

      observer.observe(video);
    });
  }

  function collapseCard(card) {
    const button = card.querySelector('[data-review-more]');
    card.classList.remove('is-open');
    card.classList.add('is-clamped');
    if (button) button.textContent = button.dataset.labelMore;
  }

  document.addEventListener('bridge:slidechange', (event) => {
    event.target.querySelectorAll('.bridge-review.is-open').forEach(collapseCard);
  });

  function setupClamp(scope) {
    scope.querySelectorAll('.bridge-review').forEach((card) => {
      const body = card.querySelector('[data-review-body]');
      const button = card.querySelector('[data-review-more]');
      if (!body || !button || button.dataset.clampReady) return;
      button.dataset.clampReady = '1';

      const measure = () => {
        if (card.classList.contains('is-open')) return;
        const target = body.querySelector('p') || body;
        const overflowing = target.scrollHeight > target.clientHeight + 1;
        card.classList.toggle('is-clamped', overflowing);
        button.classList.toggle('is-hidden', !overflowing);
      };

      button.addEventListener('click', () => {
        const open = card.classList.toggle('is-open');
        card.classList.toggle('is-clamped', !open);
        button.textContent = open ? button.dataset.labelLess : button.dataset.labelMore;
      });

      measure();
      window.addEventListener('resize', measure);

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(measure);
      }
    });
  }

  const STICKY = '[data-bridge-sticky-cta]';
  const stickies = new WeakMap();

  function buildSticky(slot) {
    if (stickies.has(slot)) return;

    const button = slot.querySelector('.amazon_button');
    if (!button || typeof window.IntersectionObserver === 'undefined') return;

    const reserve = () => {
      if (button.classList.contains('fixed')) return;
      slot.style.minHeight = `${button.offsetHeight}px`;
    };

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        button.classList.toggle('fixed', !entry.isIntersecting);
      },
      { threshold: 0 }
    );

    reserve();
    observer.observe(slot);
    window.addEventListener('resize', reserve);
    stickies.set(slot, { observer, reserve });
  }

  function destroySticky(slot) {
    const entry = stickies.get(slot);
    if (!entry) return;
    entry.observer.disconnect();
    window.removeEventListener('resize', entry.reserve);
    stickies.delete(slot);
  }

  const TIMER = '[data-bridge-timer]';
  const timers = new Set();
  const formatters = {};
  let tickId = null;

  function secondsLeft(tz) {
    try {
      if (!formatters[tz]) {
        formatters[tz] = new Intl.DateTimeFormat('en-US', {
          timeZone: tz, hourCycle: 'h23', hour: '2-digit', minute: '2-digit', second: '2-digit',
        });
      }
      const parts = formatters[tz].formatToParts(new Date());
      const get = (type) => Number(parts.find((p) => p.type === type).value);
      return 86400 - (get('hour') * 3600 + get('minute') * 60 + get('second'));
    } catch (e) {
      const d = new Date();
      return 86400 - (d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds());
    }
  }

  const pad = (n) => String(n).padStart(2, '0');

  function renderTimer(el) {
    const left = secondsLeft(el.dataset.timezone || 'America/New_York');
    const ratio = left / 86400;

    const clock = el.querySelector('[data-timer-clock]');
    if (clock) clock.textContent = `${pad(Math.floor(left / 3600))}:${pad(Math.floor((left % 3600) / 60))}:${pad(left % 60)}`;

    const bar = el.querySelector('[data-timer-bar]');
    if (bar) bar.style.width = `${ratio * 100}%`;

    const stock = el.querySelector('[data-timer-stock]');
    if (stock) {
      const start = Number(el.dataset.stockStart) || 50;
      const end = Number(el.dataset.stockEnd) || 1;
      const count = Math.max(end, Math.ceil(end + (start - end) * ratio));
      stock.textContent = stock.dataset.label.replace('[count]', count);
    }
  }

  function tick() {
    timers.forEach((el) => (el.isConnected ? renderTimer(el) : timers.delete(el)));
    if (!timers.size) { clearInterval(tickId); tickId = null; }
  }

  function setupTimers(scope) {
    scope.querySelectorAll(TIMER).forEach((el) => {
      if (timers.has(el)) return;
      timers.add(el);
      renderTimer(el);
    });
    if (timers.size && !tickId) tickId = setInterval(tick, 1000);
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { clearInterval(tickId); tickId = null; }
    else if (timers.size) { tick(); tickId = setInterval(tick, 1000); }
  });

  function apply(scope = document) {
    setupTimers(scope);
    scope.querySelectorAll(ROOT).forEach(build);
    scope.querySelectorAll(STICKY).forEach(buildSticky);
    setupClamp(scope);
    setupVideos(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => apply());
  } else {
    apply();
  }

  document.addEventListener('shopify:section:load', (event) => apply(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(ROOT).forEach(destroy);
    event.target.querySelectorAll(STICKY).forEach(destroySticky);
  });
})();
